/**
 * Sprint T-18 · Notification Studio · Template Studio editor.
 *
 * Edits ONLY overrides per tenant (Q3 = a · base templates are code, not data).
 * Wraps the T-17 endpoints:
 *   POST   /api/notifications/templates/{id}/overrides       · upsert
 *   DELETE /api/notifications/templates/{id}/overrides/{oid} · reset
 *
 * And the T-18 endpoint:
 *   POST /api/notifications/studio/validate                  · syntax + size
 *
 * The editor requires a `tenant_id` before allowing save (Q3 enforcement).
 * If the user tries to save without tenant, we show an inline warning.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Trash2, PlayCircle, AlertTriangle, Loader2, GitCompare } from "lucide-react";
import {
  useValidateTemplate,
  useRenderPreview,
  useUpsertTemplateOverride,
  useDeleteTemplateOverride,
  useTemplateOverrides,
} from "../../../hooks/useNotifications";
import TemplateDiffView from "./TemplateDiffView";

export default function TemplateStudioEditor({ template, baseContent, previewContext }) {
  const templateId = template?.template_id;
  const defaultLocale = template?.default_locale || "es-ES";

  const [tenantId, setTenantId] = useState("");
  const [locale, setLocale] = useState(defaultLocale);
  const [subject, setSubject] = useState(baseContent?.subject || "");
  const [bodyHtml, setBodyHtml] = useState(baseContent?.body_html || "");
  const [bodyText, setBodyText] = useState(baseContent?.body_text || "");
  const [showDiff, setShowDiff] = useState(false);
  const [validation, setValidation] = useState(null);
  const [preview, setPreview] = useState(null);

  const validateM = useValidateTemplate();
  const previewM = useRenderPreview();
  const upsertM = useUpsertTemplateOverride();
  const deleteM = useDeleteTemplateOverride();
  const overridesQ = useTemplateOverrides(templateId, tenantId || undefined);

  // Repoblate fields when template changes
  useEffect(() => {
    setSubject(baseContent?.subject || "");
    setBodyHtml(baseContent?.body_html || "");
    setBodyText(baseContent?.body_text || "");
    setPreview(null);
    setValidation(null);
    setShowDiff(false);
  }, [templateId, baseContent?.subject, baseContent?.body_html, baseContent?.body_text]);

  const changed = useMemo(
    () =>
      subject !== (baseContent?.subject || "") ||
      bodyHtml !== (baseContent?.body_html || "") ||
      bodyText !== (baseContent?.body_text || ""),
    [subject, bodyHtml, bodyText, baseContent],
  );

  const existingOverride = useMemo(() => {
    const items = overridesQ.data?.items || [];
    return items.find((o) => o.locale === locale && o.tenant_id === tenantId);
  }, [overridesQ.data, tenantId, locale]);

  const handleValidate = () => {
    validateM.mutate(
      {
        template_id: templateId,
        subject_override: subject !== (baseContent?.subject || "") ? subject : null,
        body_html_override: bodyHtml !== (baseContent?.body_html || "") ? bodyHtml : null,
        body_text_override: bodyText !== (baseContent?.body_text || "") ? bodyText : null,
        tenant_id: tenantId || null,
      },
      {
        onSuccess: (data) => {
          setValidation(data);
          if (data.ok) toast.success("Validación OK");
          else toast.error(`Validación falló · ${data.errors.length} error(es)`);
        },
        onError: () => toast.error("Error al validar"),
      },
    );
  };

  const handlePreview = () => {
    if (!tenantId) {
      toast.info("Preview del template base (sin tenant seleccionado)");
    }
    previewM.mutate(
      {
        template_id: templateId,
        context: previewContext || {},
        tenant_id: tenantId || null,
        locale,
      },
      {
        onSuccess: (data) => setPreview(data),
        onError: () => toast.error("Preview falló"),
      },
    );
  };

  const handleSave = async () => {
    if (!tenantId) {
      toast.error("Necesitas un tenant_id · los templates base no son editables (Q3)");
      return;
    }
    if (!changed) {
      toast.info("Sin cambios respecto al template base");
      return;
    }
    try {
      await upsertM.mutateAsync({
        template_id: templateId,
        body: {
          tenant_id: tenantId,
          locale,
          subject_override: subject !== (baseContent?.subject || "") ? subject : null,
          body_html_override: bodyHtml !== (baseContent?.body_html || "") ? bodyHtml : null,
          body_text_override: bodyText !== (baseContent?.body_text || "") ? bodyText : null,
          active: true,
        },
      });
      toast.success(`Override guardado · tenant=${tenantId.slice(0, 8)}…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save falló");
    }
  };

  const handleReset = async () => {
    if (!existingOverride) {
      toast.info("No hay override para eliminar");
      return;
    }
    if (!window.confirm(`¿Eliminar override tenant=${tenantId.slice(0, 8)}…?`)) return;
    try {
      await deleteM.mutateAsync({
        template_id: templateId,
        override_id: existingOverride.id,
      });
      toast.success("Override eliminado · vuelve al template base");
      setSubject(baseContent?.subject || "");
      setBodyHtml(baseContent?.body_html || "");
      setBodyText(baseContent?.body_text || "");
    } catch (e) {
      toast.error("No se pudo eliminar");
    }
  };

  const disabledSave = !changed || !tenantId;

  return (
    <div className="flex flex-col gap-3" data-testid="studio-template-editor">
      {/* Tenant selector row */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
        <label className="flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Tenant ID (requerido para guardar)
          </span>
          <input
            type="text"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            placeholder="uuid del tenant"
            data-testid="studio-template-tenant-input"
            className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 font-mono text-[11px]"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Locale
          </span>
          <select
            value={locale}
            onChange={(e) => setLocale(e.target.value)}
            data-testid="studio-template-locale-select"
            className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 text-xs"
          >
            {Object.keys(template?.locales || { [defaultLocale]: true }).map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        </label>
        <div className="flex items-end gap-1">
          {existingOverride ? (
            <span
              className="rounded bg-emerald-500/20 px-2 py-1 text-[10px] text-emerald-300"
              data-testid="studio-template-override-badge"
            >
              override activo · #{existingOverride.id.slice(0, 8)}
            </span>
          ) : tenantId ? (
            <span className="text-[10px] text-[var(--ag-text-dim)]">
              sin override · usa el base
            </span>
          ) : null}
        </div>
      </div>

      {!tenantId && (
        <div
          className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-[11px] text-amber-200"
          data-testid="studio-template-tenant-warning"
        >
          <AlertTriangle size={12} className="mt-0.5" />
          <span>
            Los templates base no son editables (Q3 = a · versionados en Git).
            Para guardar cambios introduce un <b>tenant_id</b> y se creará un override.
          </span>
        </div>
      )}

      {/* Fields */}
      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Subject
        </span>
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="studio-template-subject-editor"
          className="rounded border border-[var(--ag-border)] bg-black/30 px-2 py-1.5 font-mono text-[11px]"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Body HTML
        </span>
        <textarea
          rows={10}
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          spellCheck={false}
          data-testid="studio-template-body-html-editor"
          className="rounded border border-[var(--ag-border)] bg-black/30 px-3 py-2 font-mono text-[11px] leading-relaxed"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[9px] uppercase tracking-widest text-[var(--ag-text-dim)]">
          Body text (opcional · fallback multipart)
        </span>
        <textarea
          rows={5}
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          spellCheck={false}
          data-testid="studio-template-body-text-editor"
          className="rounded border border-[var(--ag-border)] bg-black/30 px-3 py-2 font-mono text-[11px] leading-relaxed"
        />
      </label>

      {/* Validation output */}
      {validation && (
        <div
          className={`rounded border p-2 text-[11px] ${
            validation.ok
              ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
              : "border-rose-500/40 bg-rose-500/10 text-rose-200"
          }`}
          data-testid="studio-template-validation-output"
        >
          <div className="font-mono">
            {validation.ok ? "✓ OK" : `${validation.errors.length} error(es)`}
          </div>
          {validation.errors?.map((e, i) => (
            <div key={i} className="font-mono text-[10px]">
              · {e.field}: {e.msg}
            </div>
          ))}
          {validation.warnings?.map((w, i) => (
            <div key={`w${i}`} className="font-mono text-[10px] text-amber-200">
              ⚠ {w.field}: {w.msg}
            </div>
          ))}
          {validation.variables_detected?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
              Variables:{" "}
              {validation.variables_detected.map((v) => (
                <span key={v} className="rounded bg-black/40 px-1 py-0.5 font-mono">
                  {v}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleValidate}
          disabled={validateM.isPending}
          data-testid="studio-template-validate-btn"
          className="flex items-center gap-1.5 rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/60 hover:text-[var(--ag-text)]"
        >
          {validateM.isPending ? <Loader2 size={11} className="animate-spin" /> : "✓"}
          Validate
        </button>
        <button
          type="button"
          onClick={handlePreview}
          disabled={previewM.isPending}
          data-testid="studio-template-preview-btn"
          className="flex items-center gap-1.5 rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/60 hover:text-[var(--ag-text)]"
        >
          <PlayCircle size={11} />
          Preview
        </button>
        <button
          type="button"
          onClick={() => setShowDiff((v) => !v)}
          data-testid="studio-template-diff-toggle"
          className="flex items-center gap-1.5 rounded border border-[var(--ag-border)] px-3 py-1.5 text-xs text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/60 hover:text-[var(--ag-text)]"
        >
          <GitCompare size={11} />
          {showDiff ? "Ocultar" : "Ver"} diff
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={disabledSave || upsertM.isPending}
          data-testid="studio-template-save-btn"
          className="flex items-center gap-1.5 rounded border border-emerald-500/60 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {upsertM.isPending ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
          Save override
        </button>
        {existingOverride && (
          <button
            type="button"
            onClick={handleReset}
            disabled={deleteM.isPending}
            data-testid="studio-template-reset-btn"
            className="flex items-center gap-1.5 rounded border border-rose-500/50 bg-rose-500/10 px-3 py-1.5 text-xs text-rose-200 hover:bg-rose-500/20"
          >
            {deleteM.isPending ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
            Reset (delete override)
          </button>
        )}
      </div>

      {/* Diff view */}
      {showDiff && (
        <div className="mt-2 space-y-2" data-testid="studio-template-diff-panel">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Diff body_html (base ↔ override)
          </div>
          <TemplateDiffView
            baseText={baseContent?.body_html || ""}
            overrideText={bodyHtml}
          />
        </div>
      )}

      {/* Preview output */}
      {preview && (
        <div className="mt-2 space-y-1" data-testid="studio-template-preview-panel">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            Preview
          </div>
          {preview.subject && (
            <div className="rounded border border-[var(--ag-border)] bg-black/20 p-2 font-mono text-[11px]">
              subject: {preview.subject}
            </div>
          )}
          {preview.body_html && (
            <iframe
              sandbox=""
              srcDoc={preview.body_html}
              title="preview"
              style={{ width: "100%", height: 320, border: "1px solid var(--ag-border)", background: "white", borderRadius: 6 }}
              data-testid="studio-template-preview-iframe"
            />
          )}
        </div>
      )}
    </div>
  );
}
